import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, Copy, Check, Minus, Plus } from 'lucide-react';
import Button from './Button';
import { formatCurrency } from '../../utils/currency';

// Building blocks shared by ProductDetailView (parts) and BOMComponent
// (assemblies), so both read as the same kind of page. Presentation only: no
// data fetching, so they can be rendered without a Supabase client.

/** Part number as the page title (mono, copyable), description, favourite toggle. */
export const ProductHeader = ({ code, description, isFavourite = false, onToggleFavourite, children }) => {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable (e.g. insecure context); the part number is still selectable.
    }
  };

  return (
    <div className="px-6 py-5 border-b border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <h1 className="text-2xl font-bold font-mono text-gray-900 break-all">{code}</h1>
          <button
            type="button"
            onClick={copyCode}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            aria-label={copied ? 'Part number copied' : 'Copy part number'}
            title={copied ? 'Copied' : 'Copy part number'}
          >
            {copied
              ? <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
              : <Copy className="w-4 h-4" aria-hidden="true" />}
          </button>
        </div>
        {description && <p className="text-gray-700 mt-1">{description}</p>}
        {children}
      </div>
      {onToggleFavourite && (
        <Button
          variant="secondary"
          onClick={onToggleFavourite}
          aria-pressed={isFavourite}
          className="flex-shrink-0"
          icon={
            <Star
              className={`w-4 h-4 ${isFavourite ? 'text-yellow-400 fill-yellow-400' : ''}`}
              aria-hidden="true"
            />
          }
        >
          {isFavourite ? 'In Favourites' : 'Add to Favourites'}
        </Button>
      )}
    </div>
  );
};

/** Customer price as the dominant figure; POA when there is none. */
export const PriceBlock = ({ price, listPrice, unit }) => {
  const buy = parseFloat(price);
  const list = parseFloat(listPrice);
  const hasPrice = buy > 0;

  return (
    <div>
      <p className="text-sm font-medium text-gray-600">Your price</p>
      {hasPrice ? (
        <p className="mt-1 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-gray-900 tabular-nums">{formatCurrency(buy)}</span>
          {unit && <span className="text-sm text-gray-600">per {unit}</span>}
        </p>
      ) : (
        <>
          <p className="mt-1 text-3xl font-bold text-gray-900">POA</p>
          <p className="text-sm text-gray-600 mt-1">
            Price on application. Add this part to an inquiry list and we'll quote it.
          </p>
        </>
      )}
      {list > 0 && list !== buy && (
        <p className="text-sm text-gray-600 mt-1">
          List price <span className="tabular-nums">{formatCurrency(list)}</span>
        </p>
      )}
    </div>
  );
};

/**
 * Quantity field with stepper buttons. The text is held locally so the field
 * can be cleared and retyped; the quantity only changes on a valid number.
 */
export const QuantityInput = ({ id, value, onChange, label = 'Quantity' }) => {
  const [text, setText] = useState(String(value));

  // Follow quantity changes made elsewhere (stepper buttons, parent resets).
  useEffect(() => {
    setText((current) => (parseInt(current, 10) === value ? current : String(value)));
  }, [value]);

  const handleChange = (raw) => {
    setText(raw);
    const parsed = parseInt(raw, 10);
    if (parsed >= 1) onChange(parsed);
  };

  const stepButton = 'px-3 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="inline-flex items-stretch rounded-lg border border-gray-300 bg-white">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, value - 1))}
          disabled={value <= 1}
          className={`${stepButton} rounded-l-lg`}
          aria-label="Decrease quantity"
        >
          <Minus className="w-4 h-4" aria-hidden="true" />
        </button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min="1"
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => setText(String(value))}
          className="w-16 py-2.5 text-center text-sm font-medium text-gray-900 tabular-nums border-x border-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className={`${stepButton} rounded-r-lg`}
          aria-label="Increase quantity"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

/** "3 in your cart · View cart" confirmation under the add button. */
export const InCartNote = ({ quantity, unit, onViewCart }) => {
  if (!quantity) return null;
  return (
    <p className="text-sm text-gray-700 flex flex-wrap items-center gap-x-2" role="status">
      <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
      <span>
        <span className="font-semibold tabular-nums">{quantity}</span>
        {unit ? ` ${unit}` : ''} in your cart
      </span>
      {onViewCart && (
        <button
          type="button"
          onClick={onViewCart}
          className="font-medium text-gray-900 underline decoration-brand decoration-2 underline-offset-4 hover:decoration-brand-hover"
        >
          View cart
        </button>
      )}
    </p>
  );
};
